import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Ship } from '../../interfaces/types';
import { FaPercent, FaHashtag } from 'react-icons/fa';
import './ShipStats.css';
import { ImageCache } from '../../services/imageCache';
import { useNavigate } from 'react-router-dom';
import { TYPE_TRANSLATIONS, SIZE_TRANSLATIONS, SIZE_ORDER } from '../../utils/translations';

type ShipSize = 'vehicle' | 'snub' | 'small' | 'medium' | 'large' | 'capital' | 'N' | 'V' | 'S' | 'M' | 'L' | 'C';

interface ShipStatsProps {
  ships: Ship[];
  showUserStats: boolean;
}

interface ChartData {
  name: string;
  value: number;
  percentage?: number;
}

interface StatChartProps {
  data: ChartData[];
  title: string;
  showPercentage: boolean;
  onToggleView: () => void;
  className?: string;
}

const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8',
  '#82ca9d', '#ffc658', '#ff7300', '#a4de6c', '#d0ed57'
];

const StatChart: React.FC<StatChartProps> = ({ data, title, showPercentage, onToggleView, className }) => {
  const navigate = useNavigate();
  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  const handleBarClick = (entry: any) => {
    const filterValue = entry.name;
    let path = '/flota?';

    switch (title) {
      case "Naves por Usuario":
        path += `user=${filterValue === 'Anónimo' ? 'anonymous' : filterValue}`;
        break;
      case "Naves por Tamaño":
        // Convertir el nombre en español al valor en inglés
        const sizeKey = Object.entries(SIZE_TRANSLATIONS)
          .find(([_, spanish]) => spanish === filterValue)?.[0];
        path += `size=${sizeKey?.toLowerCase() || filterValue.toLowerCase()}`;
        break;
      case "Naves por Tipo":
        // Convertir el nombre en español al valor en inglés
        const typeKey = Object.entries(TYPE_TRANSLATIONS)
          .find(([_, spanish]) => spanish === filterValue)?.[0];
        path += `type=${typeKey?.toLowerCase() || filterValue.toLowerCase()}`;
        break;
      case "Naves por Fabricante":
        // Usar el código del fabricante (originalValue) en lugar del nombre
        path += `manufacturer=${entry.originalValue}`;
        break;
      default:
        return;
    }

    navigate(path);
  };

  const chartData = data.map(item => ({
    ...item,
    percentage: (item.value / total) * 100
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0];
      const name = dataPoint.payload.name;
      const value = dataPoint.value;

      return (
        <div className="custom-tooltip">
          <p className="value">
            {showPercentage 
              ? `${name}: ${value.toFixed(1)}%`
              : `${name}: ${value} unidades`
            }
          </p>
        </div>
      );
    }
    return null;
  };



  const renderChart = () => {
    if (showPercentage) {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="percentage"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={100}
              onClick={handleBarClick}
              label={({
                cx,
                cy,
                midAngle,
                innerRadius,
                outerRadius,
                value,
                name
              }) => {
                const RADIAN = Math.PI / 180;
                const radius = outerRadius + 25;
                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                const y = cy + radius * Math.sin(-midAngle * RADIAN);
                return (
                  <text
                    x={x}
                    y={y}
                    fill="white"
                    textAnchor={x > cx ? 'start' : 'end'}
                    dominantBaseline="central"
                    fontSize={12}
                  >
                    {`${name} (${Math.round(value)}%)`}
                  </text>
                );
              }}
            >
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={COLORS[index % COLORS.length]}
                  style={{ cursor: 'pointer' }}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={400}>
        <BarChart 
          data={chartData} 
          margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="name" 
            angle={-45}
            textAnchor="end"
            height={100}
            interval={0}
            tick={{ fontSize: 12, fill: '#a89984' }}
          />
          <YAxis 
            tick={{ fontSize: 12, fill: '#a89984' }}
            allowDecimals={false}
          />
          <Tooltip 
            content={<CustomTooltip />}
            formatter={(value, name, props) => {
              const itemName = props.payload.name;
              return [`${value} unidades`, itemName];
            }}
            cursor={{ fill: 'rgba(255, 255, 255, 0.1)' }}
          />
          <Bar
            dataKey="value" 
            fill="#82ca9d"
            onClick={handleBarClick}
          >
            {chartData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={COLORS[index % COLORS.length]}
                style={{ cursor: 'pointer' }}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div className={`ship-stat-card ${className || ''}`}>
      <div className="stat-header">
        <h3>{title}</h3>
        <div className="stat-controls">
          <span className="total-circle">
            {total}
          </span>
          <button 
            className="view-toggle"
            onClick={onToggleView}
            title={showPercentage ? "Mostrar valores" : "Mostrar porcentajes"}
          >
            <FaHashtag />
            <span className="separator" />
            <FaPercent />
          </button>
        </div>
      </div>
      {renderChart()}
    </div>
  );
};

const CARGO_RANGES = [
  { label: '0', min: 0, max: 0 },
  { label: '1-100', min: 1, max: 100 },
  { label: '101-200', min: 101, max: 200 },
  { label: '201-400', min: 201, max: 400 },
  { label: '401-800', min: 401, max: 800 },
  { label: '801-1600', min: 801, max: 1600 },
  { label: '1601-4000', min: 1601, max: 4000 },
  { label: '4001-10000', min: 4001, max: 10000 },
  { label: '10001-50000', min: 10001, max: 50000 },
  { label: 'Más de 50000', min: 50001, max: Infinity }
];

const getCargoStats = (ships: Ship[]) => {
  const stats = CARGO_RANGES.map(range => ({
    name: range.label,
    value: ships.filter(ship => {
      const cargo = ship.cargo_capacity || 0;
      return cargo >= range.min && cargo <= range.max;
    }).length
  }));

  return {
    data: stats,
    total: stats.reduce((sum, item) => sum + item.value, 0)
  };
};

export const ShipStats: React.FC<ShipStatsProps> = ({ ships, showUserStats }) => {
  const [showPercentages, setShowPercentages] = useState({
    manufacturers: false,
    users: false,
    types: false,
    sizes: false,
    crew: false,
    focus: false,
    repeated: false,
    cargo: false,
    insurance: false,
    purchaseType: false
  });

  const getTypeStats = () => {
    const stats = ships.reduce((acc, ship) => {
      if (!ship.type) return acc;
      const type = ship.type.trim().toLowerCase();
      const translatedType = TYPE_TRANSLATIONS[type] || type;
      if (!acc[translatedType]) {
        acc[translatedType] = {
          value: 0,
          originalValue: type
        };
      }
      acc[translatedType].value += 1;
      return acc;
    }, {} as Record<string, { value: number, originalValue: string }>);

    const data = Object.entries(stats)
      .map(([name, { value, originalValue }]) => ({ 
        name, 
        value,
        originalValue 
      }))
      .sort((a, b) => b.value - a.value);

    return {
      data,
      total: data.reduce((sum, item) => sum + item.value, 0)
    };
  };

  const getSizeStats = () => {
    const stats = ships.reduce((acc, ship) => {
      if (!ship.size) return acc;
      const size = ship.size.trim().toLowerCase();
      const translatedSize = SIZE_TRANSLATIONS[size] || size;
      if (!acc[translatedSize]) {
        acc[translatedSize] = {
          value: 0,
          originalValue: size
        };
      }
      acc[translatedSize].value += 1;
      return acc;
    }, {} as Record<string, { value: number, originalValue: string }>);

    const data = Object.entries(stats)
      .map(([name, { value, originalValue }]) => ({ 
        name, 
        value,
        originalValue 
      }))
      .sort((a, b) => {
        const aOrder = Object.entries(SIZE_TRANSLATIONS)
          .find(([_, val]) => val === a.name)?.[0];
        const bOrder = Object.entries(SIZE_TRANSLATIONS)
          .find(([_, val]) => val === b.name)?.[0];
        
        return (SIZE_ORDER[aOrder as ShipSize] || 0) - (SIZE_ORDER[bOrder as ShipSize] || 0);
      });

    return {
      data,
      total: data.reduce((sum, item) => sum + item.value, 0)
    };
  };

  const getCrewStats = () => {
    const stats = ships.reduce((acc, ship) => {
      const minCrew = ship.min_crew || 1;
      const crewKey = `${minCrew} tripulantes`;
      if (!acc[crewKey]) {
        acc[crewKey] = {
          value: 0,
          originalValue: minCrew.toString()
        };
      }
      acc[crewKey].value += 1;
      return acc;
    }, {} as Record<string, { value: number, originalValue: string }>);

    const data = Object.entries(stats)
      .map(([name, { value, originalValue }]) => ({ 
        name, 
        value,
        originalValue 
      }))
      .sort((a, b) => {
        const aNum = parseInt(a.originalValue);
        const bNum = parseInt(b.originalValue);
        return aNum - bNum;
      });

    return {
      data,
      total: data.reduce((sum, item) => sum + item.value, 0)
    };
  };

  const getFocusStats = () => {
    const stats = ships.reduce((acc, ship) => {
      acc[ship.focus] = (acc[ship.focus] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    return {
      data: Object.entries(stats).map(([name, value]) => ({ name, value })),
      total: Object.values(stats).reduce((a, b) => a + b, 0)
    };
  };

  const getManufacturerStats = () => {
    const stats = ships.reduce((acc, ship) => {
      const code = ship.manufacturer.code;
      if (!acc[code]) {
        acc[code] = {
          name: ship.manufacturer.name,
          value: 0
        };
      }
      acc[code].value += 1;
      return acc;
    }, {} as Record<string, { name: string, value: number }>);

    const data = Object.entries(stats)
      .map(([code, { name, value }]) => ({ 
        name,
        value,
        originalValue: code
      }))
      .sort((a, b) => b.value - a.value);

    return {
      data,
      total: data.reduce((sum, item) => sum + item.value, 0)
    };
  };

  const getUserStats = () => {
    const stats = ships.reduce((acc, ship) => {
      const user = ship.usuario || 'anonymous';
      acc[user] = (acc[user] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      data: Object.entries(stats)
        .map(([name, value]) => ({ 
          name: name === 'anonymous' ? 'Anónimo' : name,
          value 
        }))
        .sort((a, b) => b.value - a.value),
      total: Object.values(stats).reduce((a, b) => a + b, 0)
    };
  };

  const togglePercentage = (chart: keyof typeof showPercentages) => {
    setShowPercentages(prev => ({
      ...prev,
      [chart]: !prev[chart]
    }));
  };

  const manufacturerStats = getManufacturerStats();
  const typeStats = getTypeStats();
  const sizeStats = getSizeStats();
  const crewStats = getCrewStats();
  const focusStats = getFocusStats();
  const userStats = getUserStats();

  const repeatedShipsStats = React.useMemo(() => {
    const counts = ships.reduce((acc, ship) => {
      acc[ship.name] = (acc[ship.name] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const data = Object.entries(counts)
      .filter(([_, count]) => count > 1)
      .map(([name, count]) => ({
        name,
        value: count,
        percentage: (count / ships.length) * 100
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    return { data };
  }, [ships]);

  const cargoStats = getCargoStats(ships);

  const insuranceStats = React.useMemo(() => {
    const counts = ships.reduce((acc, ship) => {
      const insurance = ship.insurance;
      if (!insurance) {
        acc['Sin seguro'] = (acc['Sin seguro'] || 0) + 1;
      } else if (insurance.toLowerCase() === 'lti') {
        acc['LTI'] = (acc['LTI'] || 0) + 1;
      } else {
        // Extraer número de meses del seguro
        const months = insurance.match(/(\d+)/);
        if (months) {
          const monthCount = parseInt(months[1]);
          const label = `${monthCount} ${monthCount === 1 ? 'mes' : 'meses'}`;
          acc[label] = (acc[label] || 0) + 1;
        } else {
          acc['Otro'] = (acc['Otro'] || 0) + 1;
        }
      }
      return acc;
    }, {} as Record<string, number>);

    const data = Object.entries(counts)
      .map(([name, value]) => ({
        name,
        value,
        percentage: (value / ships.length) * 100
      }))
      .sort((a, b) => {
        // Ordenar: LTI primero, luego por número de meses (descendente), luego otros
        if (a.name === 'LTI') return -1;
        if (b.name === 'LTI') return 1;
        if (a.name === 'Sin seguro') return 1;
        if (b.name === 'Sin seguro') return -1;
        if (a.name === 'Otro') return 1;
        if (b.name === 'Otro') return -1;
        
        const aMonths = parseInt(a.name.match(/(\d+)/)?.[1] || '0');
        const bMonths = parseInt(b.name.match(/(\d+)/)?.[1] || '0');
        return bMonths - aMonths;
      });

    return { data };
  }, [ships]);

  const purchaseTypeStats = React.useMemo(() => {
    const counts = ships.reduce((acc, ship) => {
      if (ship.ingame === true) {
        acc['In-Game'] = (acc['In-Game'] || 0) + 1;
      } else if (ship.ingame === false || ship.ingame === undefined) {
        acc['Dinero Real'] = (acc['Dinero Real'] || 0) + 1;
      } else {
        acc['No especificado'] = (acc['No especificado'] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    const data = Object.entries(counts)
      .map(([name, value]) => ({
        name,
        value,
        percentage: (value / ships.length) * 100
      }))
      .sort((a, b) => {
        // Ordenar: Dinero Real primero, luego In-Game, luego No especificado
        if (a.name === 'Dinero Real') return -1;
        if (b.name === 'Dinero Real') return 1;
        if (a.name === 'In-Game') return -1;
        if (b.name === 'In-Game') return 1;
        return 0;
      });

    return { data };
  }, [ships]);

  return (
    <div className="ship-stats">
      <div className="ship-stats-grid">
        <StatChart
          data={manufacturerStats.data}
          title="Naves por Fabricante"
          showPercentage={showPercentages.manufacturers}
          onToggleView={() => togglePercentage('manufacturers')}
        />
        {showUserStats && (
          <StatChart
            data={userStats.data}
            title="Naves por Usuario"
            showPercentage={showPercentages.users}
            onToggleView={() => togglePercentage('users')}
          />
        )}
        <StatChart
          data={typeStats.data}
          title="Naves por Tipo"
          showPercentage={showPercentages.types}
          onToggleView={() => togglePercentage('types')}
        />
        <StatChart
          data={sizeStats.data}
          title="Naves por Tamaño"
          showPercentage={showPercentages.sizes}
          onToggleView={() => togglePercentage('sizes')}
        />
        <StatChart
          data={crewStats.data}
          title="Naves por Tripulación"
          showPercentage={showPercentages.crew}
          onToggleView={() => togglePercentage('crew')}
        />
        <StatChart
          data={repeatedShipsStats.data}
          title="Naves más Repetidas"
          showPercentage={showPercentages.repeated}
          onToggleView={() => togglePercentage('repeated')}
        />
        <StatChart
          data={cargoStats.data}
          title="Distribución por Carga"
          showPercentage={showPercentages.cargo}
          onToggleView={() => togglePercentage('cargo')}
        />
        <StatChart
          data={focusStats.data}
          title="Naves por Enfoque"
          showPercentage={showPercentages.focus}
          onToggleView={() => togglePercentage('focus')}
          className="full-width"
        />
        <StatChart
          data={insuranceStats.data}
          title="Naves por Seguro"
          showPercentage={showPercentages.insurance}
          onToggleView={() => togglePercentage('insurance')}
        />
        <StatChart
          data={purchaseTypeStats.data}
          title="Naves por Tipo de Compra"
          showPercentage={showPercentages.purchaseType}
          onToggleView={() => togglePercentage('purchaseType')}
        />
       
      </div>
    </div>
  );
}; 